import React, { useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import SignaturePad from 'signature_pad'

const SIGNATURE_URL = 'signature_url'
const CANVAS_WIDTH = 300
const CANVAS_HEIGHT = 150
const END_STROKE = 'endStroke'

const SIGNED_BY_INPUT_CLASS = 'form-control tw:flex-1 tw:rounded! tw:border-gray-300! tw:bg-white! '
  + 'tw:h-9! tw:py-1! tw:text-sm! tw:text-gray-800!'
const PREVIEW_BOX_CLASS = 'tw:flex tw:h-41 tw:w-75 tw:items-center tw:justify-center tw:overflow-hidden '
  + 'tw:rounded! tw:border tw:border-solid tw:border-gray-300 tw:bg-white'
const CANVAS_CLASS = 'tw:block tw:rounded! tw:border tw:border-solid tw:border-gray-300 tw:touch-none'
const ERASE_LINK_CLASS = 'tw:self-start tw:border-0 tw:bg-transparent tw:p-0 tw:cursor-pointer '
  + 'tw:text-sm tw:text-gray-950 tw:underline'

const SignatureMarkField = ({ mark, readonly, onChange }) => {
  const canvasRef = useRef(null)
  const padRef = useRef(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const signatureUrl = mark[SIGNATURE_URL]
  const cleared = mark.signatureData === null
  const showCard = Boolean(signatureUrl) && !cleared
  const showCanvas = !readonly && !showCard

  const handleName = useCallback(event => onChangeRef.current({ value: event.target.value }), [])

  const handleEnd = useCallback(() => onChangeRef.current({ signatureData: padRef.current.toDataURL() }), [])

  const handleDelete = useCallback(() => onChangeRef.current({ signatureData: null }), [])

  const handleErase = useCallback(() => {
    padRef.current.clear()
    onChangeRef.current({ signatureData: null })
  }, [])

  useEffect(() => {
    let pad = null
    if (showCanvas) {
      const canvas = canvasRef.current
      canvas.width = CANVAS_WIDTH
      canvas.height = CANVAS_HEIGHT
      pad = new SignaturePad(canvas)
      padRef.current = pad
      pad.addEventListener(END_STROKE, handleEnd)
    }

    return () => {
      if (pad) {
        pad.off()
        pad.removeEventListener(END_STROKE, handleEnd)
      }
    }
  }, [showCanvas, handleEnd])

  if (readonly) {
    if (!signatureUrl)
      return <div className="tw:text-sm tw:text-gray-600">Aucune signature</div>

    return (
      <div className="tw:flex tw:lg:w-1/2 tw:flex-col tw:gap-4">
        <div className="tw:text-sm tw:text-gray-950">{`Signé par ${mark.value || '—'}`}</div>
        <div className={PREVIEW_BOX_CLASS}>
          <img
            alt="Signature"
            className="tw:max-h-full tw:max-w-full tw:object-contain"
            draggable={false}
            src={signatureUrl}
          />
        </div>
      </div>
    )
  }

  if (showCard) {
    return (
      <div className="tw:flex tw:lg:w-1/2 tw:flex-col tw:gap-4">
        <div className="tw:flex tw:items-center tw:gap-1">
          <span className="tw:shrink-0 tw:text-sm tw:text-gray-950">Signé par</span>
          <input
            className={SIGNED_BY_INPUT_CLASS}
            maxLength={60}
            placeholder="Nom du signataire (optionnel)"
            type="text"
            value={mark.value || ''}
            onChange={handleName}
          />
        </div>
        <div className="tw:flex tw:flex-col tw:gap-1">
          <div className={PREVIEW_BOX_CLASS}>
            <img
              alt="Signature"
              className="tw:max-h-full tw:max-w-full tw:object-contain"
              draggable={false}
              src={signatureUrl}
            />
          </div>
          <button className={ERASE_LINK_CLASS} type="button" onClick={handleDelete}>
            Effacer la signature
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="tw:flex tw:lg:w-1/2 tw:flex-col tw:gap-4">
      <div className="tw:flex tw:items-center tw:gap-1">
        <span className="tw:shrink-0 tw:text-sm tw:text-gray-950">Signé par</span>
        <input
          className={SIGNED_BY_INPUT_CLASS}
          maxLength={60}
          placeholder="Nom du signataire (optionnel)"
          type="text"
          value={mark.value || ''}
          onChange={handleName}
        />
      </div>
      <div className="tw:flex tw:flex-col tw:items-start tw:gap-1">
        <canvas ref={canvasRef} className={CANVAS_CLASS} />
        <button className={ERASE_LINK_CLASS} type="button" onClick={handleErase}>
          Effacer la signature
        </button>
      </div>
    </div>
  )
}

SignatureMarkField.propTypes = {
  mark: PropTypes.shape({
    [SIGNATURE_URL]: PropTypes.string,
    value: PropTypes.string
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  readonly: PropTypes.bool
}

SignatureMarkField.defaultProps = {
  readonly: false
}

export default SignatureMarkField
